import { useEffect, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";

import { useTranslation } from "next-i18next";

import useCheckVatLazy from "@/Apollo/hooks/useCheckVatLazy";
import {
  AccountBusinessSettingsFormData,
  AccountSettingsViewProps,
} from "@/common/contracts/account.contracts";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { AccountSettingsWrapper } from "@/components/Account/AccountSettingsWrapper";
import useMe from "@/hooks/useMe";
import useMerchant from "@/hooks/useMerchant";
import useUpdateMerchant from "@/scenes/auth/hooks/useUpdateMerchant";
import addToast from "@/toast/addToast";
import { Tooltip } from "@/ui/Tooltip";
import { Button } from "@/ui/cva/Button";
import { CheckBox } from "@/ui/cva/check-box/CheckBox";
import { Input } from "@/ui/cva/input/Input";
import { InputGroup } from "@/ui/cva/input/InputGroup";
import { InputGroupErrorHelper } from "@/ui/cva/input/InputGroupErrorHelper";
import { InputLabel } from "@/ui/typography/InputLabel";
import { MerchantType } from "@whoppah/apollo/schema";
import InfoIcon from "@whoppah/icons/24/Info";

/**
 * Business details, and for a private seller the UPGRADE to a business account.
 *
 * WHO-5386. This page has always performed the upgrade: submitting it sends
 * `type: MerchantType.Business` whatever the account was before. It was only
 * ever shown to accounts that were already a business, so the one screen that
 * could serve a private seller was invisible to exactly them, and the request
 * became a CS handoff where somebody made the change by hand.
 *
 * Widening the nav gate alone would have been worse than the bug: a private
 * seller would land on a form titled "Business settings" whose "Save changes"
 * button silently converts the account and raises what they pay us by about
 * half again. So the individual case is its own mode, and it does two things
 * the business case does not.
 *
 *  1. It states the fee change BEFORE the button, in money terms rather than
 *     as a policy sentence.
 *  2. It requires an explicit confirmation. Nothing converts without it.
 *
 * One direction only. There is no path here from business back to private:
 * that changes the fee, the VAT posture and the consumer-law footing of every
 * future sale, so it stays with a human. The gateway will happily write the
 * type either way, so this restraint has to live in the UI.
 */
export const AccountSettingsBusinessSettings = ({
  title,
}: AccountSettingsViewProps) => {
  const { t } = useTranslation();

  const { me } = useMe();

  const { id: merchantId, merchant, defaultAddress } = useMerchant();
  const country = defaultAddress?.country;

  // Absence is treated as individual, so an unread merchant gets the guarded
  // path rather than the silent one.
  const isUpgrade = merchant?.type !== MerchantType.Business;

  const [isBusy, setIsBusy] = useState(false);
  const [upgradeConfirmed, setUpgradeConfirmed] = useState(false);
  const [checkVat] = useCheckVatLazy();
  const [updateMerchant] = useUpdateMerchant();

  const {
    register,
    setValue,
    getValues,
    handleSubmit: formHandleSubmit,
    formState: { errors },
    setError,
  } = useForm<AccountBusinessSettingsFormData>();

  useEffect(() => {
    if (!me || !me.merchants.length) {
      return;
    }

    setValue("taxId", me.merchants[0]?.tax_id ?? "");
    setValue("vatId", me.merchants[0]?.vat_id ?? "");
  }, [me, setValue]);

  const tryValidateVat = async (vatNumber: string) => {
    try {
      const { data } = await checkVat({ variables: { vat: vatNumber } });

      if (data?.checkVat?.result === "INVALID") {
        setError("vatId", {
          type: "manual",
          message: t("account:your-account-settings-error-invalid-vat"),
        });

        return false;
      }

      // We are being optimistic here:
      // - if the VAT cannot be validated (result FAILED) we are still going to save it.
      return true;
    } catch (ex) {
      console.error(
        "AccountSettingsBusinessDetails:tryValidateVat",
        (ex as Error).message
      );

      return false;
    }
  };

  const handleError = (errors: FieldErrors) => {
    /** Trigger `form_error` event(s). */
    analyticsService.trackAllFormErrors("account_business_details", errors);
  };

  const handleSubmit = async (data: AccountBusinessSettingsFormData) => {
    // Belt and braces: the button is disabled without the tick, but a converted
    // account cannot be undone from here, so the handler refuses too.
    if (isUpgrade && !upgradeConfirmed) {
      return;
    }

    setIsBusy(true);

    try {
      const vat = data.vatId;

      const isVatValid = await (vat ? tryValidateVat(vat) : true);

      if (!isVatValid) {
        return;
      }

      await updateMerchant({
        variables: {
          id: merchantId,
          name: merchant?.name,
          type: MerchantType.Business,
          tax_id: data.taxId,
          vat_id: vat ?? null,
        },
      });
      addToast(
        isUpgrade
          ? t(
              "account:your-account-settings-upgraded-to-business",
              "Your account is now a business account."
            )
          : t("account:your-account-settings-saved-business-details"),
        { appearance: "success" }
      );
    } catch (ex) {
      console.error(
        "AccountSettingsBusinessDetails:handleSubmit",
        (ex as Error).message
      );
      addToast(
        t("account:your-account-settings-error-saving-business-details"),
        { appearance: "error" }
      );
    } finally {
      setIsBusy(false);
    }
  };

  const handleSaveClick = () => {
    // "Normalize" values before they are validated.
    const taxNumber = getValues("taxId");
    const vatNumber = getValues("vatId");

    if (taxNumber) {
      setValue("taxId", taxNumber.trim());
    }
    if (vatNumber) {
      setValue("vatId", vatNumber.replaceAll(/\s/g, ""));
    }
  };

  return (
    <AccountSettingsWrapper title={title}>
      <hr className="border-x-0 border-b border-t-0" />
      {isUpgrade && (
        <div className="mb-6 rounded-lg bg-brand-100 p-4 text-black">
          <p className="font-semibold">
            {t(
              "account:your-account-settings-upgrade-title",
              "Switch to a business account"
            )}
          </p>
          <p className="mt-2 text-sm">
            {t(
              "account:your-account-settings-upgrade-explainer",
              "Business accounts can sell under a company name and add their VAT details to invoices. Fill in your details below to switch."
            )}
          </p>
          {/* The number the seller is actually agreeing to. Stated in money
              terms and before the button, never after.

              Source of truth for both rates is the gateway,
              src/common/static/fee.static.ts:
                INDIVIDUAL_MERCHANT_SELLER_FEE = 12.5
                BUSINESS_MERCHANT_SELLER_FEE   = 16
              and the gateway applies the new rate itself whenever the type
              changes. They are repeated here because the web schema does not
              expose merchant.fee_amount; if those constants ever move, this
              copy and the test that pins it must move with them. */}
          <p className="mt-3 text-sm font-semibold">
            {t(
              "account:your-account-settings-upgrade-fee-notice",
              "This changes your seller fee. Private accounts pay 12.5% including VAT. Business accounts pay 16% excluding VAT, which is 19.36% including VAT at the Dutch rate."
            )}
          </p>
          <p className="mt-2 text-sm">
            {t(
              "account:your-account-settings-upgrade-one-way-notice",
              "Switching back to a private account is not something you can do here. Contact us if you need to change it later."
            )}
          </p>
        </div>
      )}
      <form
        onSubmit={formHandleSubmit(handleSubmit, handleError)}
        className="flex flex-col space-y-6"
      >
        <div className="grid gap-x-6 gap-y-4 md:grid-cols-2">
          <InputGroup
            valid={!errors.taxId}
            disabled={isBusy}
            label={props => (
              <div className="flex items-center space-x-1">
                <InputLabel className="text-base" {...props}>
                  {t("account:your-account-settings-tax-number")}
                </InputLabel>
                <Tooltip
                  side="top"
                  sideOffset={10}
                  content={
                    <span>
                      {t("account:your-account-settings-tax-number-tooltip")}
                    </span>
                  }
                >
                  <div>
                    <InfoIcon className="h-5 w-5" />
                  </div>
                </Tooltip>
              </div>
            )}
            input={props => (
              <Input
                placeholder={t("account:your-account-settings-tax-number")}
                {...register("taxId", {
                  /** Required only for Netherlands. */
                  required: country === "NL",
                })}
                {...props}
              />
            )}
            helper={props => (
              <InputGroupErrorHelper {...props} error={errors.taxId} />
            )}
          />
          <InputGroup
            valid={!errors.vatId}
            disabled={isBusy}
            label={props => (
              <div className="flex items-center space-x-1">
                <InputLabel className="text-base" {...props}>
                  {t("account:your-account-settings-vat-number")}
                </InputLabel>
                <Tooltip
                  side="top"
                  sideOffset={10}
                  content={
                    <span>
                      {t("account:your-account-settings-vat-number-tooltip")}
                    </span>
                  }
                >
                  <div>
                    <InfoIcon className="h-5 w-5" />
                  </div>
                </Tooltip>
              </div>
            )}
            input={props => (
              <Input
                placeholder={t("account:your-account-settings-vat-number")}
                {...register("vatId")}
                {...props}
              />
            )}
            helper={props => (
              <InputGroupErrorHelper {...props} error={errors.vatId} />
            )}
          />
        </div>
        {isUpgrade && (
          <label className="flex cursor-pointer items-start space-x-3">
            <CheckBox
              color="brand"
              checked={upgradeConfirmed}
              disabled={isBusy}
              onChange={event => setUpgradeConfirmed(event.target.checked)}
            />
            <span className="text-sm">
              {t(
                "account:your-account-settings-upgrade-confirm",
                "I understand that my account becomes a business account and that my seller fee changes to 16% excluding VAT."
              )}
            </span>
          </label>
        )}
        <Button
          type="submit"
          size="large"
          loading={isBusy}
          disabled={isUpgrade && !upgradeConfirmed}
          className="self-start"
          onClick={handleSaveClick}
        >
          {isUpgrade
            ? t(
                "account:your-account-settings-upgrade-submit",
                "Switch to a business account"
              )
            : t("account:your-account-settings-save-changes")}
        </Button>
      </form>
    </AccountSettingsWrapper>
  );
};
